(() => {
  if (window.CartGiftRecommendations || !window.CartGiftEligibility) return;

  const MAX_SEEDS = 6;
  const REQUEST_TIMEOUT = 6500;
  const CATALOG_LIMIT = 24;
  const aiContextCache = new Map();
  const pendingAiContexts = new Map();
  const resultCache = new Map();
  const pendingRequests = new Map();

  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const toNumericId = (value) => {
    const parsed = Number.parseInt(String(value || '').split('/').pop() || '0', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };

  const toFamily = (value) => normalizeText(String(value || '').split('|')[0]);

  const isRewardGift = (item) =>
    String(item?.properties?._reward_gift || '') === 'true';

  const getItemFamily = (item) =>
    toFamily(item?.properties?._reward_family || item?.product_title || item?.title);

  const getPaidItems = (cart) => window.CartGiftEligibility.getPaidItems(cart);

  const getCatalogAssignments = (picker) =>
    String(picker?.dataset.giftCatalogHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

  const hash = (value) => {
    let result = 2166136261;
    String(value || '').split('').forEach((character) => {
      result ^= character.charCodeAt(0);
      result = Math.imul(result, 16777619);
    });
    return result >>> 0;
  };

  const getFingerprint = (cart, audience = 'default') => {
    const paidLines = getPaidItems(cart)
      .map((item) => [
        Number(item?.product_id) || 0,
        Number(item?.variant_id || item?.id) || 0,
        Math.max(0, Number(item?.quantity) || 0),
      ].join(':'))
      .sort()
      .join('|');
    const rewardLines = (cart?.items || [])
      .filter(isRewardGift)
      .map((item) => [
        Number(item?.product_id) || 0,
        Number(item?.variant_id || item?.id) || 0,
        Math.max(0, Number(item?.quantity) || 0),
        Number.parseInt(item?.properties?._reward_tier || '0', 10) || 0,
        getItemFamily(item),
      ].join(':'))
      .sort()
      .join('|');
    return `${hash(cart?.token)}:${audience}:paid=${paidLines}:rewards=${rewardLines}`;
  };

  const fetchJson = async (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        ...options,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Recommendation request returned ${response.status}.`);
      return response.json();
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const getVariantOptions = (variant, optionCount) => {
    if (Array.isArray(variant?.options) && variant.options.length) {
      return variant.options.map(String);
    }
    return [variant?.option1, variant?.option2, variant?.option3]
      .slice(0, optionCount)
      .map((value) => String(value || ''));
  };

  const normalizeCatalogProduct = (product) => {
    const productId = toNumericId(product?.id);
    const optionNames = (product?.options || [])
      .map((option) => String(option?.name || option || '').trim())
      .filter(Boolean);
    const variants = (product?.variants || [])
      .map((variant) => ({
        available: variant?.available !== false,
        id: toNumericId(variant?.id),
        options: getVariantOptions(variant, optionNames.length),
        title: String(variant?.title || '').trim(),
      }))
      .filter((variant) => variant.id && variant.available);
    const image =
      product?.featured_image?.src ||
      product?.featured_image ||
      product?.images?.[0]?.src ||
      product?.images?.[0] ||
      '';

    if (!productId || !product?.title || !image || !variants.length) return null;
    return {
      family: toFamily(product.title),
      handle: String(product.handle || ''),
      image: String(image),
      optionNames,
      productId,
      title: String(product.title),
      variants,
      collectionHandles: [],
    };
  };

  const fetchCatalog = async (picker) => {
    const baseUrl = picker?.dataset.giftCatalogUrl;
    const catalogAssignments = getCatalogAssignments(picker);
    const collectionHandles = [...new Set(catalogAssignments)];
    const primaryCollectionHandle = catalogAssignments[0] || '';
    const priorityHandles = String(picker?.dataset.giftPriorityHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
    const requests = [];

    collectionHandles.forEach((collectionHandle) => {
      const url = new URL(
        `/collections/${encodeURIComponent(collectionHandle)}/products.json`,
        window.location.origin
      );
      url.searchParams.set('limit', String(CATALOG_LIMIT));
      requests.push(
        fetchJson(url.href).then((data) => ({
          collectionHandle,
          products: data?.products || [],
        }))
      );
    });

    if (!collectionHandles.length && baseUrl) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('limit', String(CATALOG_LIMIT));
      requests.push(
        fetchJson(url.href).then((data) => ({
          collectionHandle: '',
          products: data?.products || [],
        }))
      );
    }
    priorityHandles.forEach((handle) => {
      const url = new URL(`/products/${encodeURIComponent(handle)}.js`, window.location.origin);
      requests.push(
        fetchJson(url.href).then((product) => ({
          collectionHandle: primaryCollectionHandle,
          products: [product],
        }))
      );
    });
    const responses = await Promise.allSettled(requests);
    const catalog = new Map();
    responses
      .filter((response) => response.status === 'fulfilled')
      .forEach((response) => {
        const collectionHandle = response.value.collectionHandle;
        response.value.products
          .map(normalizeCatalogProduct)
          .filter(Boolean)
          .forEach((product) => {
            const existing = catalog.get(product.productId);
            const assignedCollections = new Set(existing?.collectionHandles || []);
            if (collectionHandle) assignedCollections.add(collectionHandle);
            catalog.set(product.productId, {
              ...(existing || product),
              collectionHandles: [...assignedCollections],
            });
          });
      });
    return [...catalog.values()];
  };

  const requestAiRecommendations = (picker, shopDomain, productIds) => {
    const endpoint = picker?.dataset.aiUpsellsUrl;
    if (!endpoint || !shopDomain || !productIds.length) return Promise.resolve(null);
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('shop', shopDomain);
    url.searchParams.set('productId', '');
    url.searchParams.set('customerId', String(window.Shopify?.customer_id || ''));
    url.searchParams.set('cartIds', productIds.join(','));
    url.searchParams.set('liquidHistory', '');
    url.searchParams.set('localHistory', '[]');
    url.searchParams.set('t', String(Date.now()));
    return fetchJson(url.href);
  };

  const fetchAiContext = async (picker, cart) => {
    const emptyContext = {
      blockedFamilies: [],
      blockedProductIds: [],
      candidateResponses: [],
    };
    if (picker?.dataset.aiUpsellsEnabled === 'false') return emptyContext;
    const shopDomain = picker?.dataset.shopDomain;
    const productIds = [
      ...new Set(getPaidItems(cart).map((item) => Number(item?.product_id)).filter(Boolean)),
    ].sort((first, second) => first - second).slice(0, MAX_SEEDS);
    if (!shopDomain || !productIds.length) return emptyContext;
    const contextKey = [
      picker?.dataset.aiUpsellsUrl || '',
      shopDomain,
      productIds.join(','),
    ].join('|');
    if (aiContextCache.has(contextKey)) return aiContextCache.get(contextKey);
    if (pendingAiContexts.has(contextKey)) return pendingAiContexts.get(contextKey);

    const request = Promise.allSettled([
        requestAiRecommendations(picker, shopDomain, productIds),
        ...productIds.map((productId) =>
          requestAiRecommendations(picker, shopDomain, [productId])
        ),
      ])
      .then((results) => {
        const combinedResponse = results[0].status === 'fulfilled' ? results[0].value : null;
        const recommendedProducts = combinedResponse?.recommendations || [];
        const context = {
          blockedFamilies: recommendedProducts
            .map((product) => toFamily(product?.title))
            .filter(Boolean),
          blockedProductIds: recommendedProducts
            .map((product) => toNumericId(product?.id))
            .filter(Boolean),
          candidateResponses: results
            .slice(1)
            .filter((result) => result.status === 'fulfilled' && result.value)
            .map((result) => result.value),
        };
        if (combinedResponse) aiContextCache.set(contextKey, context);
        return context;
      })
      .finally(() => pendingAiContexts.delete(contextKey));
    pendingAiContexts.set(contextKey, request);
    return request;
  };

  const mergeCandidates = (catalog, aiResponses) => {
    const candidates = new Map();
    catalog.forEach((product) => {
      candidates.set(product.productId, {
        ...product,
        aiMentions: 0,
        aiScore: 0,
      });
    });

    aiResponses.forEach((response) => {
      (response?.recommendations || []).forEach((rawProduct, index, recommendations) => {
        const productId = toNumericId(rawProduct?.id);
        if (!productId) return;
        const catalogProduct = candidates.get(productId);
        if (!catalogProduct) return;
        const existing = catalogProduct;
        const associationCount = Math.max(0, Number(rawProduct?.associationCount) || 0);
        existing.aiMentions += 1;
        existing.aiScore += associationCount * 100 + recommendations.length - index;
        candidates.set(productId, existing);
      });
    });
    return [...candidates.values()];
  };

  const getGiftSlotHandles = (assignments, seed) => {
    const counts = new Map();
    assignments.forEach((handle) => counts.set(handle, (counts.get(handle) || 0) + 1));
    const handles = [...counts.keys()].sort((first, second) =>
      (counts.get(second) || 0) - (counts.get(first) || 0) ||
      hash(`${seed}:${first}`) - hash(`${seed}:${second}`)
    );
    if (!handles.length) return [];

    const slots = handles.slice(0, 3);
    const allocated = new Map(slots.map((handle) => [handle, 1]));
    const totalWeight = handles.reduce((total, handle) => total + counts.get(handle), 0);
    while (slots.length < 3) {
      const nextSlotNumber = slots.length + 1;
      const nextHandle = handles
        .slice()
        .sort((first, second) => {
          const firstDeficit = (counts.get(first) / totalWeight) * nextSlotNumber - (allocated.get(first) || 0);
          const secondDeficit = (counts.get(second) / totalWeight) * nextSlotNumber - (allocated.get(second) || 0);
          return (
            secondDeficit - firstDeficit ||
            hash(`${seed}:extra:${first}`) - hash(`${seed}:extra:${second}`)
          );
        })[0];
      slots.push(nextHandle);
      allocated.set(nextHandle, (allocated.get(nextHandle) || 0) + 1);
    }
    return slots;
  };

  const chooseProducts = ({
    blockedRecommendationFamilies,
    blockedRecommendationProductIds,
    cart,
    catalogAssignments,
    candidates,
    fingerprint,
    priorityHandles,
    tier,
  }) => {
    const rewardItems = (cart?.items || []).filter(isRewardGift);
    const blockedProductIds = new Set(
      rewardItems.map((item) => Number(item?.product_id)).filter(Boolean)
    );
    const blockedFamilies = new Set(
      rewardItems.map(getItemFamily).filter(Boolean)
    );
    blockedRecommendationProductIds.forEach((productId) => blockedProductIds.add(productId));
    blockedRecommendationFamilies.forEach((family) => blockedFamilies.add(family));
    const priority = new Set(priorityHandles);
    const seed = `${fingerprint}:${tier}`;

    const eligible = candidates.filter(
      (candidate) =>
        candidate?.productId &&
        candidate?.family &&
        candidate?.image &&
        candidate?.variants?.length &&
        !blockedProductIds.has(candidate.productId) &&
        !blockedFamilies.has(candidate.family)
    );

    eligible.sort((first, second) => {
      const firstAi = first.aiMentions > 0 ? 1 : 0;
      const secondAi = second.aiMentions > 0 ? 1 : 0;
      const firstPriority = priority.has(first.handle) ? 1 : 0;
      const secondPriority = priority.has(second.handle) ? 1 : 0;
      return (
        secondAi - firstAi ||
        second.aiScore - first.aiScore ||
        secondPriority - firstPriority ||
        hash(`${seed}:${first.productId}`) - hash(`${seed}:${second.productId}`)
      );
    });

    const selected = [];
    const selectedFamilies = new Set();
    const selectFirstMatching = (predicate) => {
      const candidate = eligible.find(
        (product) => !selectedFamilies.has(product.family) && predicate(product)
      );
      if (!candidate) return;
      selected.push(candidate);
      selectedFamilies.add(candidate.family);
    };

    getGiftSlotHandles(catalogAssignments, seed).forEach((collectionHandle) => {
      if (selected.length >= 3) return;
      selectFirstMatching((candidate) =>
        candidate.collectionHandles.includes(collectionHandle)
      );
    });

    eligible.forEach((candidate) => {
      if (selected.length >= 3 || selectedFamilies.has(candidate.family)) return;
      selected.push(candidate);
      selectedFamilies.add(candidate.family);
    });
    return selected;
  };

  const buildRecommendations = async ({ cart, picker, tier }) => {
    const audience = picker?.dataset.giftAudience || 'default';
    const fingerprint = getFingerprint(cart, audience);
    const catalogAssignments = getCatalogAssignments(picker);
    const [catalogResult, aiResult] = await Promise.allSettled([
      fetchCatalog(picker),
      fetchAiContext(picker, cart),
    ]);
    const catalog = catalogResult.status === 'fulfilled' ? catalogResult.value : [];
    const aiContext = aiResult.status === 'fulfilled'
      ? aiResult.value
      : { blockedFamilies: [], blockedProductIds: [], candidateResponses: [] };
    const candidates = mergeCandidates(catalog, aiContext.candidateResponses);
    const priorityHandles = String(picker?.dataset.giftPriorityHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
    const products = chooseProducts({
      blockedRecommendationFamilies: new Set(aiContext.blockedFamilies),
      blockedRecommendationProductIds: new Set(aiContext.blockedProductIds),
      cart,
      catalogAssignments,
      candidates,
      fingerprint,
      priorityHandles,
      tier,
    });

    return {
      fingerprint,
      products,
      source: products.some((product) => product.aiMentions > 0)
        ? 'ai-upsells'
        : 'catalog-fallback',
    };
  };

  const getRecommendations = ({ cart, picker, tier }) => {
    const audience = picker?.dataset.giftAudience || 'default';
    const key = [
      picker?.dataset.aiUpsellsUrl || '',
      picker?.dataset.giftCatalogUrl || '',
      picker?.dataset.giftCatalogHandles || '',
      getFingerprint(cart, audience),
      tier,
    ].join('|');
    if (resultCache.has(key)) return Promise.resolve(resultCache.get(key));
    if (pendingRequests.has(key)) return pendingRequests.get(key);

    const request = buildRecommendations({ cart, picker, tier })
      .then((result) => {
        if (result.products.length === 3) resultCache.set(key, result);
        return result;
      })
      .finally(() => pendingRequests.delete(key));
    pendingRequests.set(key, request);
    return request;
  };

  window.CartGiftRecommendations = Object.freeze({
    clear: () => {
      aiContextCache.clear();
      resultCache.clear();
    },
    getFingerprint,
    getRecommendations,
  });
})();
